import React from "react";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Money, MoneyText } from "@workspace/accounting/components";
import type { Money as MoneyType } from "@workspace/accounting/types";
import { CheckOperation } from "@workspace/operations/check-operation";
import { genid } from "@workspace/shared/utils";
import { TerminalPayment } from "@workspace/stripe";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";

import { eposApi } from "../../api";
import type { TabDetail, TabPaymentResponse } from "../../types";
import type { PaymentMethods } from "../epos-context";
import { useEposContext } from "../epos-context";
import {
	parseTabAmount,
	payTabBlock,
	payTabBlockMessage,
	payTabFailure,
	tabPaymentLines,
} from "../pay-tab";
import { AddTip } from "./add-tip";
import { PrintTabButton } from "./print-tab-button";

const formatAmount = (money: MoneyType) => (money.value / 100).toFixed(2);

/**
 * HA-554 Part 2 (planning, comment 8291 step 13) — Pay Tab, from tab detail: any number of payments, each of any amount up
 * to what the tab owes, by cash or card, until it owes nothing. What the tab owes and how each payment was allocated
 * across its rounds are the server's (its answer, and the tab read), never worked out on the till.
 *
 * Server-confirmed (D-HA-24): a payment is pending while it's in flight, can't be submitted twice, and shows only once the
 * server has recorded it. A Retry sends the same payment again (its id), so one that did land isn't taken twice. The tab
 * closes when the server says so. Refused offline (an offline Pay Tab cash payment needs HA-164's queue), and while an
 * order on the tab has lines the server doesn't have yet.
 */
export function PayTabDialog(props: {
	tabId: string | null;
	onOpenChange: (open: boolean) => void;
}) {
	// review2 on #439, F1: the dialog stays mounted between tabs, so each tab gets its own state (the amount, the notes
	// tendered, the payment id): a key per tab, rather than resetting each by hand.
	return <PayTabDialogBody key={props.tabId ?? ""} {...props} />;
}

function PayTabDialogBody({
	tabId,
	onOpenChange,
}: {
	tabId: string | null;
	onOpenChange: (open: boolean) => void;
}) {
	const {
		tenant,
		venueId,
		hasUnsentLines,
		defaultCurrency,
		paymentProvider,
		// review2 on #439: the EPOS context's card hook, the one its stranded-payment recovery knows is being collected, so a
		// Pay Tab card past 15 seconds is never "recovered" from under the reader.
		cardPayments: card,
		data: { terminals, tipsEnabled },
		operations,
	} = useEposContext();
	const queryClient = useQueryClient();
	const tabQueryKey = [tenant, venueId, "epos", "tabs", tabId ?? ""];

	// HA-360 Part B — the payment that settled the tab, confirmed in the dialog with its Tab receipt to print: what it was.
	const [settledBy, setSettledBy] = React.useState<string | null>(null);

	// A card payment Stripe has taken is recorded on the tab a moment later (the server records it after the card
	// payment saga): the tab is read again until it shows.
	const [awaitingCard, setAwaitingCard] = React.useState<string | null>(null);

	const detailQuery = useQuery({
		...eposApi.getTabOptions(tenant, venueId, tabId ?? ""),
		enabled: !!tabId,
	});
	const detail = detailQuery.data;

	// The amount to pay starts at what the tab owes, and starts there again once a payment changes that.
	const outstanding = detail?.figures.outstanding;
	const [edited, setEdited] = React.useState<{
		text: string;
		owed: number;
	} | null>(null);
	const amountText =
		outstanding && edited?.owed !== outstanding.value
			? formatAmount(outstanding)
			: (edited?.text ?? "");

	const amount = outstanding ? parseTabAmount(amountText, outstanding) : null;

	const [tendered, setTendered] = React.useState(0);
	// HA-859: a tip on this payment, in pence, with Add tip as on an order; split over the orders it pays on the server.
	const [tip, setTip] = React.useState(0);
	const tipMoney = amount && tip > 0 ? { ...amount, value: tip } : undefined;
	const [pending, setPending] = React.useState(false);
	const submitting = React.useRef(false);
	const paymentId = React.useRef(genid());

	// review2 on #439, F2: a Failed card payment is shown, with Try again; it isn't still being collected.
	const cardFailed = card.status === "Failed";
	const [startingCard, setStartingCard] = React.useState(false);
	const collectingCard =
		(card.status !== "None" && !cardFailed) ||
		awaitingCard !== null ||
		startingCard;

	const block = detail
		? payTabBlock(detail, { online: navigator.onLine, hasUnsentLines })
		: null;
	const methods =
		(paymentProvider.paymentMethods as PaymentMethods[] | null) || [];
	const canPay =
		!!detail &&
		!block &&
		!!amount &&
		!pending &&
		!collectingCard &&
		!cardFailed;

	/** The dialog closes (and lets go of a failed card payment) only while nothing is in flight. */
	const close = () => {
		if (pending || collectingCard) return;
		if (cardFailed) card.reset();
		onOpenChange(false);
	};

	/**
	 * The server's tab after a payment: shown, and the EPOS read refreshed. A payment that closed the tab is confirmed in the
	 * dialog, which offers its Tab receipt (HA-360 Part B).
	 */
	const settled = (tab: TabDetail, what: string) => {
		queryClient.setQueryData(tabQueryKey, tab);
		setTip(0);
		void queryClient.invalidateQueries({ queryKey: [tenant, venueId, "epos"] });

		if (tab.tab.status === "Closed") {
			setSettledBy(what);
		} else {
			toast.success("Payment recorded", { description: what });
		}
	};

	const payCash = async (request: {
		paymentId: string;
		amount: MoneyType;
		tendered: MoneyType;
		tip?: MoneyType;
	}) => {
		if (submitting.current || !tabId) return;
		submitting.current = true;
		setPending(true);
		try {
			const answer: TabPaymentResponse = await eposApi.payTabCash(
				tenant,
				venueId,
				tabId,
				request,
			);
			paymentId.current = genid();
			setTendered(0);
			settled(
				answer.tab,
				answer.change && answer.change.value > 0
					? `Change due: ${formatAmount(answer.change)}`
					: "No change due",
			);
		} catch (error) {
			// review2 on #439, F3: whatever happened, read the tab again. A failure that said nothing is retried as the same
			// payment (it may have landed); a final answer moves to a new payment, so a changed tender is never refused as one
			// already recorded under the old id.
			void queryClient.invalidateQueries({ queryKey: tabQueryKey });
			const failure = payTabFailure(error);
			if (failure.transient) {
				toast.error(failure.title, {
					description: failure.description,
					action: { label: "Retry", onClick: () => void payCash(request) },
				});
			} else {
				paymentId.current = genid();
				setTendered(0);
				toast.error(failure.title, { description: failure.description });
			}
		} finally {
			submitting.current = false;
			setPending(false);
		}
	};

	/** Notes are tendered until they cover the amount and any tip; then the cash is recorded, with its change. */
	const tender = (value: number) => {
		if (!amount || !canPay) return;
		const total = tendered + value;
		if (total < amount.value + tip) {
			setTendered(total);
			return;
		}
		void payCash({
			paymentId: paymentId.current,
			amount,
			tendered: { ...amount, value: total },
			tip: tipMoney,
		});
	};

	// review2 on #439 (should-fix): one start at a time. A second press while the first is in flight would start a second
	// card payment, whose orphaned intent then holds the tab.
	const startingRef = React.useRef(false);
	const payByCard = async () => {
		if (!amount || !canPay || !tabId || startingRef.current) return;
		startingRef.current = true;
		setStartingCard(true);
		try {
			// HA-859: the card is charged the payment and its tip; the server splits the tip over the orders paid.
			await card.start({
				tabId,
				id: genid(),
				amount: { ...amount, value: amount.value + tip },
				tip: tipMoney,
			});
		} catch (error) {
			card.reset();
			void queryClient.invalidateQueries({ queryKey: tabQueryKey });
			const failure = payTabFailure(error);
			toast.error(
				failure.transient
					? "The card payment couldn't be started"
					: failure.title,
				{ description: failure.description },
			);
		} finally {
			startingRef.current = false;
			setStartingCard(false);
		}
	};

	/** A card payment taken: the tab is read until it shows it recorded, then it's settled like cash. */
	const awaitRecorded = async (id: string) => {
		setAwaitingCard(id);
		try {
			try {
				for (let attempt = 0; attempt < 30; attempt++) {
					const tab = await queryClient.fetchQuery({
						...eposApi.getTabOptions(tenant, venueId, tabId ?? ""),
						staleTime: 0,
					});
					const recorded = tab.tab.payments.find((p) => p.tabPaymentId === id);
					if (recorded) {
						settled(tab, `Card ${formatAmount(recorded.amount)}`);
						return;
					}
					await new Promise((resolve) => setTimeout(resolve, 1000));
				}
			} catch {
				// review2 on #439 (should-fix): a read that fails says so below, rather than ending quietly.
			}
			void queryClient.invalidateQueries({
				queryKey: [tenant, venueId, "epos"],
			});
			toast.error("The card payment hasn't shown on the tab yet", {
				description:
					"It was taken. Check the tab again in a moment before taking anything more.",
			});
		} finally {
			setAwaitingCard(null);
		}
	};

	const lines = detail ? tabPaymentLines(detail) : [];

	if (settledBy !== null && tabId) {
		return (
			<Dialog
				open
				onOpenChange={(open) =>
					open ? onOpenChange(true) : onOpenChange(false)
				}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Pay tab</DialogTitle>
						<DialogDescription>
							{detail ? detail.tab.name || `#${detail.tab.number}` : ""}
						</DialogDescription>
					</DialogHeader>
					<div role="status" className="space-y-1">
						<p className="font-medium">Tab paid and closed</p>
						<p className="text-sm text-muted-foreground">{settledBy}</p>
					</div>
					<DialogFooter className="gap-2">
						<CheckOperation
							operation="staff-terminal-print-receipt"
							operations={operations}
						>
							<PrintTabButton tabId={tabId} label="Print Tab receipt" />
						</CheckOperation>
						<Button onClick={() => onOpenChange(false)}>Done</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={!!tabId}
			onOpenChange={(open) => (open ? onOpenChange(true) : close())}
		>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Pay tab</DialogTitle>
					<DialogDescription>
						{detail ? detail.tab.name || `#${detail.tab.number}` : ""}
					</DialogDescription>
				</DialogHeader>

				{!detail ? (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="space-y-4">
						<dl className="grid grid-cols-2 gap-1 text-sm">
							<dt className="text-muted-foreground">Outstanding</dt>
							<dd
								className="text-right font-medium"
								data-testid="pay-tab-outstanding"
							>
								<Money {...detail.figures.outstanding} />
							</dd>
						</dl>

						{lines.length > 0 && (
							<ul
								aria-label="Payments on this tab"
								className="space-y-1 text-sm"
							>
								{lines.map((line) => (
									<li
										key={line.tabPaymentId}
										className="rounded-md border px-3 py-2"
									>
										<span className="font-medium">
											{line.method === "card" ? "Card" : "Cash"}
										</span>{" "}
										<Money {...line.amount} />
										<span className="block text-xs text-muted-foreground">
											{line.shares.map((share, i) => (
												<React.Fragment key={share.round}>
													{i > 0 && ", "}
													Round {share.round} <Money {...share.amount} />
												</React.Fragment>
											))}
										</span>
									</li>
								))}
							</ul>
						)}

						{block && !collectingCard && (
							<p role="alert" className="text-sm text-amber-600">
								{payTabBlockMessage[block]}
							</p>
						)}

						{card.status === "Initiated" ? (
							<TerminalPayment
								terminals={terminals}
								amount={card.amount?.value || 0}
								currency={card.amount?.currency || "GBP"}
								orderId={tabId ?? ""}
								onProcessingChange={card.setProcessing}
								onCancelReady={card.setReaderCancel}
								// HA-859: the server's call on the reader's tip screen (none while the tab has a service charge).
								skipTipping={card.skipTipping ?? true}
								onSuccess={async (paymentIntentId: string) => {
									const id = card.paymentId!;
									try {
										await card.success(paymentIntentId);
									} catch {
										toast.error("The card payment couldn't be recorded", {
											description:
												"The card may have been charged. Don't take the payment again — check the tab first.",
										});
										return;
									}
									card.reset();
									await awaitRecorded(id);
								}}
								onError={(error: Error) => card.setError(error)}
								tenant={tenant}
								clientSecret={card.clientSecret}
							/>
						) : cardFailed ? (
							<div role="alert" className="space-y-2 text-sm">
								<p className="text-destructive">
									{card.errorMessage || "The card payment couldn't be taken."}
								</p>
								<Button variant="outline" onClick={() => card.reset()}>
									Try again
								</Button>
							</div>
						) : collectingCard ? (
							<div className="flex items-center gap-2 text-sm text-muted-foreground">
								<Loader2 className="size-4 animate-spin" />
								{awaitingCard
									? "Recording the card payment…"
									: "Starting the card payment…"}
							</div>
						) : (
							<>
								<div className="space-y-1">
									<Label htmlFor="pay-tab-amount">Amount to pay</Label>
									<Input
										id="pay-tab-amount"
										inputMode="decimal"
										value={amountText}
										disabled={pending}
										aria-describedby="pay-tab-amount-help"
										onChange={(e) => {
											setEdited({
												text: e.target.value,
												owed: detail.figures.outstanding.value,
											});
											setTendered(0);
										}}
									/>
									{amountText !== "" && !amount && (
										<p
											id="pay-tab-amount-help"
											className="text-xs text-destructive"
										>
											Enter an amount up to{" "}
											<Money {...detail.figures.outstanding} />
										</p>
									)}
									{tendered > 0 && amount && (
										<p className="text-xs text-muted-foreground">
											Tendered <Money {...amount} value={tendered} />
										</p>
									)}
								</div>

								{/* HA-859: a tip on this payment, at most the payment; none while the venue takes no tips. */}
								<AddTip
									enabled={!!tipsEnabled}
									owed={amount ?? detail.figures.outstanding}
									tip={tip}
									onChange={setTip}
								/>

								{methods.includes("cash") && (
									<div className="space-y-2">
										<h4 className="text-sm font-medium">Cash</h4>
										<div className="grid grid-cols-3 gap-2">
											{(defaultCurrency.denominations ?? [])
												.filter((d) => d.active)
												.map((denomination) => (
													<Button
														key={denomination.name}
														variant="outline"
														className="h-12"
														disabled={!canPay}
														onClick={() => tender(denomination.value)}
													>
														<MoneyText
															{...denomination}
															currency={defaultCurrency.code}
														/>
													</Button>
												))}
											<Button
												variant="outline"
												className="h-12"
												disabled={!canPay}
												onClick={() =>
													amount &&
													void payCash({
														paymentId: paymentId.current,
														amount,
														tendered: { ...amount, value: amount.value + tip },
														tip: tipMoney,
													})
												}
											>
												Exact
											</Button>
										</div>
									</div>
								)}

								{methods.includes("card") && (
									<Button
										className="h-12 w-full"
										disabled={!canPay}
										onClick={() => void payByCard()}
									>
										<CreditCard className="mr-2 size-5" />
										Pay by card
										{amount && (
											<>
												{" "}
												<Money {...amount} />
											</>
										)}
									</Button>
								)}
							</>
						)}
					</div>
				)}

				<DialogFooter>
					{card.status === "Initiated" ? (
						<Button
							variant="outline"
							onClick={async () => {
								const result = await card.cancel();
								if (!result.cancelled)
									toast.error("The card payment can't be cancelled now", {
										description:
											result.message ?? "The reader may be taking the card.",
									});
							}}
						>
							Cancel card payment
						</Button>
					) : (
						<Button
							variant="outline"
							disabled={pending || collectingCard}
							onClick={close}
						>
							Close
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
